<template>
  <el-row :gutter="15">
    <el-col :span="24">
      <el-card shadow="never">
        <el-table
          v-loading="isLoading"
          :data="blacklist"
          stripe
          class="w-100"
        >
          <el-table-column
            label="Email"
            prop="email"
            :formatter="row => row.email || '-'"
          />
          <el-table-column
            label="Phone"
            prop="phone_number"
            :formatter="row => row.phone_number || '-'"
          />
          <el-table-column
            label="Reason"
            prop="reason"
            :formatter="row => row.reason || '-'"
          />
          <el-table-column
            label="Created By"
            :formatter="row => row.profile ? row.profile.name_full || row.profile.email || '-' : '-'"
          />
          <el-table-column
            label="Created"
            prop="created_at"
            :formatter="row => row.created_at ? $moment(row.created_at).fromNow() : '-'"
          />
          <el-table-column
            v-if="canUpdate || canDelete"
            label="Actions"
            align="right"
            width="180"
          >
            <template slot-scope="scope">
              <el-button
                v-if="canUpdate"
                size="mini"
                @click.native="edit(scope.row)"
              >
                Edit
              </el-button>
              <el-button
                v-if="canDelete"
                size="mini"
                type="danger"
                @click.native="remove(scope.row)"
              >
                Delete
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <br>

        <div style="text-align: center;">
          <el-pagination
            :current-page.sync="blacklist.page"
            :page-size.sync="blacklist.limit"
            layout="total, sizes, prev, pager, next, jumper"
            :total="blacklist.total"
            @size-change="reload"
            @current-change="reload"
          />
        </div>
      </el-card>
    </el-col>

    <el-dialog
      :title="selectedEntry.id ? 'Edit Blacklist Entry' : 'Create Blacklist Entry'"
      width="620px"
      :visible.sync="isDialogVisible"
      :close-on-click-modal="false"
    >
      <el-form
        ref="blacklistForm"
        :model="selectedEntry"
        :rules="rules"
        label-width="120px"
      >
        <el-form-item
          label="Email"
          prop="email"
        >
          <el-input
            v-model="selectedEntry.email"
            placeholder="name@example.com"
          />
        </el-form-item>

        <el-form-item
          label="Phone"
          prop="phone_number"
        >
          <el-input
            v-model="selectedEntry.phone_number"
            placeholder="0412345678"
          />
        </el-form-item>

        <el-form-item label="Reason">
          <el-input
            v-model="selectedEntry.reason"
            type="textarea"
            :rows="3"
            placeholder="Reason for blacklisting"
          />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="isDialogVisible = false">
          Cancel
        </el-button>
        <el-button
          type="primary"
          :loading="isSaving"
          @click="save"
        >
          Save
        </el-button>
      </template>
    </el-dialog>
  </el-row>
</template>

<script>
export default {
  name: 'Blacklist',
  data () {
    const validateContact = (rule, value, callback) => {
      if (this.selectedEntry.email || this.selectedEntry.phone_number) {
        callback()
        return
      }

      callback(new Error('Provide at least an email or phone number'))
    }

    return {
      title: 'Blacklist',
      breadcrumb: {
        name: 'Blacklist',
        route: { name: 'configuration-blacklist' }
      },
      actions: [
        {
          hidden: false,
          name: 'Create',
          icon: ['fal', 'plus'],
          event: () => this.create()
        },
        {
          hidden: false,
          name: 'Refresh',
          icon: ['fal', 'sync'],
          event: () => this.reload()
        }
      ],
      blacklist: [],
      isLoading: false,
      isSaving: false,
      isDialogVisible: false,
      selectedEntry: new this.$sdk.notification_blacklist(),
      rules: {
        email: [
          { validator: validateContact, trigger: 'blur' }
        ],
        phone_number: [
          { validator: validateContact, trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    canUpdate () {
      return true
    },
    canDelete () {
      return true
    }
  },
  created () {
    this.load()
  },
  methods: {
    create () {
      this.selectedEntry = new this.$sdk.notification_blacklist()
      this.isDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.blacklistForm) {
          this.$refs.blacklistForm.clearValidate()
        }
      })
    },
    edit (entry) {
      this.selectedEntry = entry.clone()
      this.isDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.blacklistForm) {
          this.$refs.blacklistForm.clearValidate()
        }
      })
    },
    load () {
      this.isLoading = true

      this.$sdk.notification_blacklist.list(builder => {
        builder.include = ['profile']
        builder.order = 'created_at:desc'
      })
        .then(res => {
          this.blacklist = res
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    async reload () {
      if (!this.blacklist.reload) {
        this.load()
        return
      }

      this.isLoading = true

      try {
        await this.blacklist.reload(builder => {
          builder.include = ['profile']
          builder.order = 'created_at:desc'
        })
      } finally {
        this.isLoading = false
      }
    },
    async save () {
      const isValid = await new Promise(resolve => {
        this.$refs.blacklistForm.validate(valid => resolve(valid))
      })

      if (!isValid) {
        return
      }

      this.isSaving = true

      try {
        await this.selectedEntry.save()
        this.$message({ message: 'Blacklist entry saved', type: 'success' })
        this.isDialogVisible = false
        await this.reload()
      } catch (error) {
        this.$message.error('Failed to save blacklist entry')
      } finally {
        this.isSaving = false
      }
    },
    remove (entry) {
      this.$confirm('Are you sure you want to delete this blacklist entry?', 'Delete', {
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        type: 'warning'
      })
        .then(async () => {
          this.isLoading = true

          try {
            await entry.delete()
            this.$message({ message: 'Blacklist entry deleted', type: 'success' })
            await this.reload()
          } catch (error) {
            this.$message.error('Failed to delete blacklist entry')
          } finally {
            this.isLoading = false
          }
        })
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
</style>
